import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { BasecampLayout, AreaLayout } from '../../components/Layout';
import { PageHeader, Card, Spinner, Alert, Table, Btn, Input, Select, Badge, Modal } from '../../components/UI';
import { api, getUser } from '../../utils/api';
import PhaseOverview from '../phase/PhaseOverview';
import PhaseNotes from '../phase/PhaseNotes';

// Explore — market development, ISKLJUČIVO customer language (Faza 3A).
// Pravilo iz spec §11.1: nigde Unit sadržaj — u konsultativnoj prodaji
// rešenja nikad ne otvaraju razgovor. Naš vokabular se pojavljuje tek u Master-u.
// Sadržaj podsekcija prenet iz mockup-a; CRM i HB Clients su živi.

const SUBNAV = [
  { href: '/explore/overview', label: 'Ice Pilot as an Explorer', isHeaderLink: true },
  { header: 'Market Development' },
  { href: '/explore/about', label: 'Presenting Hansen Beck' , indent: true},
  { href: '/explore/compass', label: 'Presenting HB Compass', indent: true },
  { href: '/explore/prospecting', label: 'Prospecting Tools', indent: true},
  { href: '/explore/digital', label: 'Digital Marketing', indent: true},
  { href: '/explore/wp', label: 'White Papers', indent: true},
  ];

function Loading() {
  return <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}><Spinner /></div>;
}

function Bullets({ items }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10, marginTop: 8 }}>
      {items.map((t, i) => (
        <Card key={i} style={{ padding: '12px 16px', fontSize: '0.88rem', color: 'var(--ink-soft)' }}>▪ {t}</Card>
      ))}
    </div>
  );
}

// ── Staticke podsekcije (sadržaj iz mockup-a) ─────────────────────────────
function About() {
  return <Bullets items={[
    'Hansen Beck — First Introduction (EN · DE)',
    'History — from the ice pilots of 1911',
    'Vision, mission, values (documents in preparation)',
    'Code of honour',
    'The network: 22 countries, 5 continents',
    'Reference stories & cases',
  ]} />;
}

function CompassWhy() {
  return <Bullets items={[
    'HB Compass one-pager (customer)',
    'Why: development by approximation has to end',
    'How: measure — develop — prove',
    'What: live demo — compass.hansenbeck.com',
    'Sample gap report (anonymised)',
  ]} />;
}

function Prospecting() {
  return (
    <>
      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', marginBottom: 10 }}>Prospecting — the method</h3>
      <Card style={{ padding: '10px 18px', marginBottom: 20 }}>
        <Table
          headers={['Step', 'What happens']}
          rows={[
            ['1 · Define the Ideal Customer Profile', 'Which organisations do I want as customers — criteria, verticals, size, culture'],
            ['2 · Define the Ideal Project', 'What engagement do I want to win — scope, programme type, decision makers'],
            ['3 · Check Existing Customers', 'Search the shared HB Clients registry — references, reactivation, FAR sources'],
            ['4 · Go to the Market', 'Outreach through chosen channels — towards Fully Active Recommendation (FAR)'],
          ]}
        />
      </Card>
      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', marginBottom: 10 }}>Prospecting Tools</h3>
      <Bullets items={[
        'FAR toolkit — earning Fully Active Recommendations',
        'Diagnostic questions bank (needs-exploration, no solution vocabulary)',
        'Onion Ring (filled for HB)',
        'Prospecting e-mail templates',
        'Pricing guidance',
        'Offer & report templates',
      ]} />
      {/* Prebačena sekcija "Notes & tools" iz PhaseOverview (deljena komponenta) */}
      <PhaseNotes phase="E" />
    </>
  );
}

function Digital() {
  return <Bullets items={[
    'LinkedIn post kit (branded)',
    'Campaign visuals',
    'Content calendar',
    'Full inventory pending intranet review (digital enablement)',
  ]} />;
}

function WhitePapers() {
  return <Bullets items={[
    'Beyond the Product — The Human-centric Revolution in Business',
    'Value-based Communication (2026)',
    'From Superheroes to Heroes',
    'Charting a Course to Excellence — Talent Navigator',
    'Business Genome',
  ]} />;
}

// ── My Projects (CRM) ─────────────────────────────────────────────────────
function ProjectModal({ project, onClose, onChanged }) {
  const [nextStep, setNextStep] = useState(project.nextStep || '');
  const [party, setParty] = useState({ role: 'sponsor', name: '' });
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try { await api.explore.updateProject(project.id, { nextStep }); onChanged(); onClose(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  async function closeWon() {
    setBusy(true);
    try { await api.explore.updateProject(project.id, { closedWon: true }); onChanged(); onClose(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  async function addParty(e) {
    e.preventDefault();
    if (!party.name.trim()) return;
    try { await api.explore.addParty(project.id, party); setParty({ role: 'sponsor', name: '' }); onChanged(); }
    catch (err) { alert(err.message); }
  }

  return (
    <Modal open onClose={onClose} title={project.name}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontSize: '0.88rem', color: 'var(--ink-soft)' }}>
          Customer: <b>{project.customer}</b>{project.contact ? ` · ${project.contact}` : ''}
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {/* The stage is not typed — it moves itself through the gates. */}
          <span style={{ flex: 1, minWidth: 150, fontSize: '0.85rem', color: 'var(--ink-soft)', alignSelf: 'center' }}>Stage: <b>{project.stage || '—'}</b></span>
          <Input value={nextStep} onChange={e => setNextStep(e.target.value)} placeholder="Next step" style={{ flex: 2, minWidth: 180 }} />
        </div>
        <div>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-faint)', marginBottom: 6 }}>
            Parties
          </div>
          <div style={{ fontSize: '0.85rem', lineHeight: 1.9 }}>
            {project.parties.map(p => <div key={p.id}>▪ <b style={{ textTransform: 'capitalize' }}>{p.role}</b>: {p.name}{p.contact ? ` (${p.contact})` : ''}</div>)}
            {!project.parties.length && <span style={{ color: 'var(--ink-faint)' }}>No parties yet</span>}
          </div>
          <form onSubmit={addParty} style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <Select value={party.role} onChange={e => setParty(p => ({ ...p, role: e.target.value }))} style={{ maxWidth: 170 }}>
              <option value="sponsor">sponsor</option>
              <option value="partner">partner</option>
              <option value="participant group">participant group</option>
              <option value="mentor">mentor</option>
            </Select>
            <Input value={party.name} onChange={e => setParty(p => ({ ...p, name: e.target.value }))} placeholder="Name" style={{ flex: 1, minWidth: 140 }} />
            <Btn type="submit" size="sm" variant="outline">Add</Btn>
          </form>
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          {!project.closedWonDate && (
            <Btn variant="outline" size="sm" loading={busy} onClick={closeWon}>Agreement signed — won (Gate 4)</Btn>
          )}
          <Btn size="sm" loading={busy} onClick={save}>Save</Btn>
        </div>
      </div>
    </Modal>
  );
}

function Crm() {
  const [state, setState] = useState({ loading: true, error: null, projects: [] });
  const [form, setForm] = useState({ name: '', customer: '', contact: '' });
  const [openId, setOpenId] = useState(null);
  const user = getUser();

  const load = useCallback(() => {
    api.explore.getProjects()
      .then(d => setState({ loading: false, error: null, projects: d.projects }))
      .catch(e => setState({ loading: false, error: e.message, projects: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);

  async function add(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.customer.trim()) return;
    try { await api.explore.createProject(form); setForm({ name: '', customer: '', contact: '' }); load(); }
    catch (err) { alert(err.message); }
  }
  async function decide(id, decision) {
    try { await api.explore.decideGraduation(id, decision); load(); }
    catch (e) { alert(e.message); }
  }

  if (state.loading) return <Loading />;
  if (state.error) return <Alert type="error">{state.error}</Alert>;

  const open = state.projects.find(p => p.id === openId);
  const proposals = state.projects.filter(p => p.graduationStatus === 'proposed' && p.ownerId === user?.id);

  return (
    <>
      <Alert type="info">
        The CRM anchors on projects, not customers: one customer can carry several projects over the
        years, and a project can involve many parties — sponsors, partners, participants.
      </Alert>

      {proposals.map(p => (
        <Alert key={p.id} type="warn">
          <b>{p.customer}</b> became a client through “{p.name}” — a proposal to enter it in the shared HB Clients registry awaits your decision.{' '}
          <Btn size="sm" onClick={() => decide(p.id, 'approve')} style={{ marginLeft: 8 }}>Approve</Btn>{' '}
          <Btn size="sm" variant="outline" onClick={() => decide(p.id, 'decline')}>Decline</Btn>
        </Alert>
      ))}

      <Card style={{ padding: '10px 18px', margin: '18px 0' }}>
        <Table
          headers={['Project', 'Customer', 'Parties', 'Stage', 'Next step', '']}
          rows={state.projects.map(p => [
            <b>{p.name}</b>,
            p.customer,
            <span style={{ fontSize: '0.8rem' }}>{p.parties.map(x => `${x.role}: ${x.name}`).join(' · ') || '—'}</span>,
            <span>
              {p.stage}
              {p.graduationStatus === 'approved' && <Badge status="completed"> in registry</Badge>}
            </span>,
            p.nextStep || '—',
            <Btn size="sm" variant="outline" onClick={() => setOpenId(p.id)}>Open</Btn>,
          ])}
          emptyMessage="No projects yet — add your first prospect below"
        />
      </Card>

      <form onSubmit={add} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', maxWidth: 680 }}>
        <Input placeholder="Project" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={{ flex: 2, minWidth: 160 }} />
        <Input placeholder="Customer" value={form.customer} onChange={e => setForm(f => ({ ...f, customer: e.target.value }))} style={{ flex: 2, minWidth: 140 }} />
        <Input placeholder="Contact" value={form.contact} onChange={e => setForm(f => ({ ...f, contact: e.target.value }))} style={{ flex: 1, minWidth: 110 }} />
        <Btn type="submit">Add project</Btn>
      </form>
      <p style={{ marginTop: 16, fontSize: '0.8rem', color: 'var(--ink-faint)' }}>
        When a prospect becomes a known client with a concrete need, work continues in Master.
        On closing, graduation to the shared HB Clients registry is proposed automatically — and enters
        the registry only when approved by the Ice Pilot running that client.
      </p>
      {open && <ProjectModal project={open} onClose={() => setOpenId(null)} onChanged={load} />}
    </>
  );
}

// ── HB Clients registar ───────────────────────────────────────────────────
function Clients() {
  const [state, setState] = useState({ loading: true, error: null, clients: [] });
  const [q, setQ] = useState('');
  useEffect(() => {
    api.explore.getClients()
      .then(d => setState({ loading: false, error: null, clients: d.clients }))
      .catch(e => setState({ loading: false, error: e.message, clients: [] }));
  }, []);

  if (state.loading) return <Loading />;
  if (state.error) return <Alert type="error">{state.error}</Alert>;

  const clients = state.clients.filter(c =>
    `${c.name} ${c.industry || ''} ${c.trainings || ''}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <>
      <Alert type="info">
        One shared intelligence base for the whole HB community — who trained where, which programmes,
        who to ask. The backbone of prospecting step 3, Check Existing Customers.
      </Alert>
      <Input placeholder="Search clients…" value={q} onChange={e => setQ(e.target.value)} style={{ maxWidth: 320, margin: '16px 0' }} />
      <Card style={{ padding: '10px 18px' }}>
        <Table
          headers={['Client', 'Industry', 'Geography', 'FAR?', 'Trainings delivered', 'Active', 'Last engagement', 'HB contact']}
          rows={clients.map(c => [
            <b>{c.name}</b>, c.industry || '—', c.geography || '—', c.far || '—',
            c.trainings || '—', c.active ? 'Yes' : 'No', c.lastEngagement || '—', c.hbContact || '—',
          ])}
          emptyMessage="No clients in the registry yet"
        />
      </Card>
    </>
  );
}

// ── Explore area ──────────────────────────────────────────────────────────
const TITLES = {
  overview: 'promote & prospect',
  about: 'About Hansen Beck',
  compass: 'Why, How and What of the HB Compass',
  prospecting: 'Prospecting Tools',
  digital: 'Digital Marketing',
  wp: 'White Papers — business perspectives to share',
  crm: 'My Projects (light CRM — project level)',
  clients: 'HB Clients — shared registry',
};

export default function Explore() {
  const { sub } = useParams();
  if (!sub) return <Navigate to="/explore/overview" replace />;

  // One Workplace, One Shelf (W4): the shelf holds no customer work — project screens
  // moved to My Customers.
  if (sub === 'crm') return <Navigate to="/customers/journeys" replace />;
  if (sub === 'clients') return <Navigate to="/customers/clients" replace />;

  let body;
  if (sub === 'overview') body = <PhaseOverview phase="E" />;
  else if (sub === 'about') body = <About />;
  else if (sub === 'compass') body = <CompassWhy />;
  else if (sub === 'prospecting') body = <Prospecting />;
  else if (sub === 'digital') body = <Digital />;
  else if (sub === 'wp') body = <WhitePapers />;
  else return <Navigate to="/explore/overview" replace />;

  return (
    <BasecampLayout>
      <PageHeader
        title="Explore — promote & prospect Hansen Beck, in customer language"
      />
      {sub !== 'crm' && sub !== 'clients' && sub !== 'overview' && (
        <Alert type="info">
          No Unit content anywhere in Explore — in consultative selling, solutions never open the
          conversation. Needs first; our vocabulary appears only in Master.
        </Alert>
      )}
      <div style={{ marginTop: 18 }}>
        <AreaLayout items={SUBNAV}>{body}</AreaLayout>
      </div>
    </BasecampLayout>
  );
}
